import React, { useEffect, useState } from "react";
import { Pencil, Save, X } from "lucide-react";

export default function LegacyCodeEditor({ value, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraft(value || "");
  }, [value, isEditing]);

  const handleSave = async () => {
    if (!onSave) return;
    try {
      setIsSaving(true);
      await onSave({ legacyCode: draft });
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save legacy code:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(value || "");
    setIsEditing(false);
  };

  return (
    <div className="flex items-center gap-1.5 lg:gap-0.5 xl:gap-1 2xl:gap-1 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-700 dark:text-gray-300">
      <span className="shrink-0">Legacy Code:</span>
      {isEditing ? (
        <>
          <input
            autoFocus
            type="text"
            aria-label="Legacy Code"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleSave();
              if (event.key === "Escape") handleCancel();
            }}
            disabled={isSaving}
            className="w-32 lg:w-24 xl:w-28 2xl:w-32 px-2 lg:px-1 xl:px-1.5 py-0.5 rounded-full border border-[#4B208B] bg-white dark:bg-[#151221] text-gray-900 dark:text-white font-medium outline-none focus:ring-2 focus:ring-[#4B208B]/20"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            aria-label="Save legacy code"
            title="Save legacy code"
            className="text-[#4B208B] dark:text-purple-300 disabled:opacity-50"
          >
            <Save className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3" />
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            aria-label="Cancel legacy code edit"
            title="Cancel"
            className="text-gray-500 disabled:opacity-50"
          >
            <X className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3" />
          </button>
        </>
      ) : (
        <>
          <span className="px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-0.5 lg:py-[1px] 2xl:py-0.5 rounded-full border border-[#4B208B] text-[#4B208B] dark:text-purple-300 font-bold">
            {value || "-"}
          </span>
          <button
            type="button"
            onClick={() => {
              setDraft(value || "");
              setIsEditing(true);
            }}
            aria-label="Edit legacy code"
            title="Edit legacy code"
            className="text-[#4B208B] dark:text-purple-300 hover:opacity-75"
          >
            <Pencil className="w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5" />
          </button>
        </>
      )}
    </div>
  );
}